export const BOT_GUIDES = [
    {
        id: 'binance-api-keys',
        title: 'Binance API Keys',
        subtitle: 'Create trade-only API keys on Binance and connect them to NeuraTrader.',
        description: 'Required before any bot can place orders. Generate spot (and optional futures) keys with the right permissions — never enable withdrawals.',
        icon: 'simple-icons:binance',
        setupPath: '/settings',
        setupLabel: 'Open Settings',
        difficulty: 'Beginner',
        timeEstimate: '5–10 min',
        overview: 'NeuraTrader trades on Binance only. You keep funds on your exchange; the app uses API keys to place and manage orders. This guide walks through creating keys on Binance, locking them down safely, and pasting them into NeuraTrader Settings.',
        prerequisites: [
            'A verified Binance account with Spot trading enabled (and Futures if you plan to use futures bots).',
            'A NeuraTrader account so you can save keys under Settings.',
            '2FA enabled on Binance for account security.',
        ],
        steps: [
            {
                title: 'Sign in to Binance',
                content: 'Open [Binance](https://www.binance.com) and sign in. Complete any security checks (email, authenticator, or SMS). Prefer the official site or app — never enter keys on third-party pages that claim to “set up” your bot.',
            },
            {
                title: 'Open API Management',
                content: 'Go to your **profile menu → Account → API Management** (or search for “API Management” in Binance help). Click **Create API**. Choose a system-generated API key (recommended) rather than a self-signed key unless you know you need one.',
                tips: [
                    'Name the key clearly, e.g. “NeuraTrader spot” so you can revoke it later without guessing.',
                    'Complete Binance’s security verification to create the key.',
                ],
            },
            {
                title: 'Copy the API Key and Secret Key',
                content: 'Binance shows the **API Key** and **Secret Key** once. Copy both immediately into a password manager or paste them straight into NeuraTrader Settings. You will not see the Secret Key again after you leave the page.',
                tips: [
                    'Never screenshot secrets or send them by email or chat.',
                    'If you lose the secret, delete the key on Binance and create a new one.',
                ],
            },
            {
                title: 'Set permissions (trade only)',
                content: 'Edit the key’s restrictions. Enable **Enable Spot & Margin Trading** for spot bots. If you use futures bots on a plan that supports them, also enable **Enable Futures**. Leave **Enable Withdrawals** OFF. NeuraTrader never needs withdrawal rights — if withdrawals are enabled, revoke the key and create a new one without them.',
                tips: [
                    'Read-only keys cannot place orders; bots will fail until trading is enabled.',
                    'Avoid enabling other permissions you do not need (e.g. Universal Transfer) unless you understand the risk.',
                ],
            },
            {
                title: 'Restrict by IP (recommended)',
                content: 'If Binance offers **IP access restriction**, bind the key to your NeuraTrader server’s public IP (from your host or VPS provider). Unrestricted keys work from any IP if leaked — IP allowlists reduce that risk. Skip this only if your deployment IP changes often and you accept the trade-off.',
                tips: [
                    'Ask your operator for the server outbound IP if you self-host or use a managed NeuraTrader instance.',
                    'After changing IPs, update the allowlist or bots will get auth errors.',
                ],
            },
            {
                title: 'Paste keys into NeuraTrader Settings',
                content: 'In NeuraTrader, open **Settings**. Under **Exchange API keys**, paste the **Spot access key** and **Spot secret key**. If your plan includes futures, also fill **Futures access key** and **Futures secret key** (create a separate key on Binance with futures trading enabled if needed). Save settings. Keys are encrypted and never shared.',
                tips: [
                    'Start with spot keys only; add futures keys when you are ready to run futures bots.',
                    'After saving, open Dashboard or place a small test order via a bot with logs enabled to confirm connectivity.',
                ],
            },
            {
                title: 'Fund your wallet and verify',
                content: 'Deposit or transfer enough base/quote assets on Binance for your strategy. Confirm balances appear in NeuraTrader **Wallet** (after keys load). Then follow a bot guide — **Grid**, **Scheduled**, or **Algo** — to place your first automation.',
            },
        ],
        troubleshooting: [
            'Invalid API key / signature errors — re-copy keys (no extra spaces), confirm secret was not truncated, and save Settings again.',
            'Orders rejected for permissions — enable Spot (and Futures if needed) trading on the key; confirm withdrawals stay disabled.',
            'IP restriction errors — your server IP may have changed; update Binance IP allowlist or temporarily use unrestricted access only while testing.',
            'Futures options missing in NeuraTrader — connect futures keys and use a plan with futures enabled.',
            'Balances empty in the app — wait a moment after saving keys, refresh Wallet, and confirm funds are in the Binance Spot (or Futures) wallet you enabled.',
        ],
    },
    {
        id: 'algo-bot',
        title: 'Algo Bot',
        subtitle: 'Build one algo bot with a market signal, order templates, and buy/sell logic.',
        description: 'Best for condition-based trading. Define when to buy and sell using indicators, wallet data, and custom rules.',
        icon: 'mdi:brain',
        setupPath: '/unified-setup',
        setupLabel: 'Open Algo Bot setup',
        difficulty: 'Advanced',
        timeEstimate: '15–25 min',
        overview: 'The Algo Bot wizard lets you configure a market signal, buy and sell order templates, and buy/sell logic in one flow. Click **Create bot** once to save everything as a single bot on the Bots page.',
        prerequisites: [
            'A NeuraTrader account with an active paid plan (live bots require automation limits).',
            'Binance API keys connected in Settings — follow the [Binance API Keys](/learn/binance-api-keys) guide if you have not set them up yet.',
            'Enough quote balance on Binance for the order sizes you configure.',
            'A clear entry/exit idea — e.g. RSI oversold buy, take-profit sell.',
        ],
        steps: [
            {
                title: 'Open the Algo Bot wizard',
                content: 'From the sidebar, expand **My Bot Setup** and click **Algo Bot**. The wizard has four sections: Bot setup, Market signal, Order templates, and Buy & sell logic.',
            },
            {
                title: 'Step 1 — Bot setup & market signal',
                content: 'Name your bot, choose **Spot** or **Futures**, and pick the trading pair. In **Market signal**, set monitor type (typically Candles), interval (1m, 5m, 1h, etc.), and technical indexes. Indexes power your buy/sell conditions — wallet balances, RSI, MACD, and more.',
                tips: [
                    'Use a shorter interval (1m–5m) for fast strategies; longer intervals (1h–4h) for swing setups.',
                    'Enable logs while testing so you can debug trigger conditions.',
                ],
            },
            {
                title: 'Step 2 — Create buy and sell order templates',
                content: 'Define separate order templates for entries and exits. For each side, set order type (Market, Limit, Stop, etc.), quantity or quantity multiplier, and optional limit/stop prices. Futures orders support margin type, leverage, and reduce-only flags.',
                tips: [
                    'Start with Market orders for simplicity, then refine with Limit orders once conditions are stable.',
                    'Match quantity to your wallet size and exchange minimum lot / notional rules.',
                ],
            },
            {
                title: 'Step 3 — Buy & sell logic',
                content: 'Define **buy logic** and **sell logic** with conditions and actions. Write conditions that must be true to fire — for example, RSI below 30 for a buy, or price crossing a moving average for a sell. Add actions such as placing an order from your templates. Set **Max Open Positions** on the buy side to cap concurrent trades.',
                tips: [
                    'Test conditions with backtests (Backtest page) before activating live.',
                    'Keep buy and sell logic on the same symbol for coherent bot behavior.',
                ],
            },
            {
                title: 'Step 4 — Create bot',
                content: 'Set sharing (Private, Public, or Shared) and choose whether to start the bot when saved. Click **Create bot** — everything is saved as one bot. Manage it from **Currently Running → Bots**.',
            },
        ],
        troubleshooting: [
            'Automation not firing — verify the monitor is active, indexes match your conditions, and the symbol is correct.',
            'Order rejected — check API permissions, minimum lot size, and available wallet balance.',
            'Futures options missing — upgrade to a paid plan with futures enabled and connect futures API keys in Settings.',
        ],
    },
    {
        id: 'grid-bot',
        title: 'Grid Bot',
        subtitle: 'Automate buy-low / sell-high across a defined price range.',
        description: 'Ideal for ranging markets. Places layered orders between a lower and upper price bound.',
        icon: 'mdi:grid',
        setupPath: '/grid',
        setupLabel: 'Open Grid Bot setup',
        difficulty: 'Beginner',
        timeEstimate: '5–10 min',
        overview: 'A Grid Bot divides a price range into equal levels and places alternating buy and sell orders. As price moves within the range, the bot captures small profits on each swing. Configure the symbol, price bounds, number of levels, and order size — then activate.',
        prerequisites: [
            'Binance spot API keys connected with trading enabled — see the [Binance API Keys](/learn/binance-api-keys) guide.',
            'Sufficient base and quote balance on Binance to fund grid orders.',
            'A sideways or ranging market expectation for your chosen pair.',
        ],
        steps: [
            {
                title: 'Open Grid Bot setup',
                content: 'In the sidebar, go to **My Bot Setup → Grid Bot**. The inline setup form opens on the page (no popup required).',
            },
            {
                title: 'Select your trading pair',
                content: 'Choose a **Symbol** from the dropdown (e.g. BTCUSDT). Live price and your wallet summary for that pair appear once selected. Confirm you hold enough of the quote asset (USDT) and base asset for grid fills.',
            },
            {
                title: 'Set the price range',
                content: 'Enter a **Lower Limit** and **Upper Limit** that bracket the price zone where you want the grid to operate. The bot only runs while the market stays within this band — bestAsk above the lower limit and bestBid below the upper limit.',
                tips: [
                    'Set the range based on recent support/resistance, not all-time highs/lows.',
                    'Wider ranges with more levels spread risk; narrower ranges react faster.',
                ],
            },
            {
                title: 'Choose levels and quantity',
                content: '**Levels** sets how many grid lines to create between your bounds (minimum 3). **Quantity** is the size per grid order — enter a fixed amount or select **Min. Notional** to use the exchange minimum. The **Notional Price** field previews lower-limit exposure.',
                tips: [
                    'More levels mean smaller spacing and more frequent trades.',
                    'Ensure quantity × lower limit meets the exchange minimum notional requirement.',
                ],
            },
            {
                title: 'Activate and save',
                content: 'Toggle **Is Active** to start the grid after saving. Enable **Logs** during initial testing to trace order placement. Click **Save Grid Bot** to deploy. Manage running grids from **Currently Running → Bots**.',
            },
        ],
        troubleshooting: [
            'Min. Lot Size or Min. Notional errors — increase quantity or widen the range.',
            'Grid not trading — price may be outside your lower/upper limits; adjust bounds.',
            'Cannot edit levels on existing grid — create a new grid bot to change level count.',
        ],
    },
    {
        id: 'scheduled-bot',
        title: 'Scheduled Bot',
        subtitle: 'Execute trades on a fixed schedule or cron expression.',
        description: 'Perfect for DCA, periodic rebalancing, or time-based entries without watching charts.',
        icon: 'mdi:clock-outline',
        setupPath: '/scheduled',
        setupLabel: 'Open Scheduled Bot setup',
        difficulty: 'Intermediate',
        timeEstimate: '10–15 min',
        overview: 'A Scheduled Bot runs your automation on a timetable — either a specific date/time or a recurring cron schedule. Combine the schedule with optional conditions and one or more actions (such as placing an order from a template) to automate recurring trades.',
        prerequisites: [
            'A saved order template (create one under Order Templates if needed).',
            'Binance API keys with trading permissions — see the [Binance API Keys](/learn/binance-api-keys) guide.',
            'Understanding of your desired schedule — daily at 09:00 UTC, every Monday, etc.',
        ],
        steps: [
            {
                title: 'Open Scheduled Bot setup',
                content: 'Navigate to **My Bot Setup → Scheduled Bot** in the sidebar. The automation form opens inline on the page.',
            },
            {
                title: 'Select symbol and name',
                content: 'Pick the **Symbol** for your scheduled trades. Give the automation a recognizable name so you can identify it in the Bots list later.',
            },
            {
                title: 'Define the schedule',
                content: 'Use **Schedule by Date & Time** for a one-off execution, or switch to **Schedule by CRON** for recurring runs. Cron examples: `0 0 9 * * *` (daily at 09:00), `0 0 9 * * 1` (every Monday at 09:00). The toggle button switches between date picker and cron input modes.',
                tips: [
                    'Cron uses six fields: second, minute, hour, day of month, month, day of week.',
                    'Use UTC when planning schedules — exchange servers typically run on UTC.',
                ],
            },
            {
                title: 'Set conditions (optional)',
                content: 'Add **conditions** if the trade should only run when market rules are met — e.g. only buy if wallet balance exceeds a threshold. Leave conditions empty to always execute on schedule. Use the condition builder with indexes from your monitor data.',
            },
            {
                title: 'Configure actions',
                content: 'Add one or more **actions** — typically **Place Order** linked to an existing order template. You can chain multiple actions for complex workflows. Each action references templates you created under Order Templates.',
                tips: [
                    'Create and test your order template first with a small quantity.',
                    'Use Market orders on schedules to guarantee execution timing.',
                ],
            },
            {
                title: 'Activate the bot',
                content: 'Enable **Is Active** and **Logs** as needed, then save. The bot appears in **Currently Running → Bots** with a SCHEDULED status when active. Pause it anytime by toggling inactive.',
            },
        ],
        troubleshooting: [
            'Schedule not running — confirm cron syntax, bot is active, and server time is UTC.',
            'Action failed — verify the linked order template exists and API keys are valid.',
            'Skipped execution — optional conditions may not have been met at run time; check logs.',
        ],
    },
];

export function getGuideById(guideId) {
    return BOT_GUIDES.find((guide) => guide.id === guideId) || null;
}